import { useCallback, useState } from 'react';
import { useProvince } from '../hooks/useProvince';
import { formatNumber, formatNetworthPerAcre, formatCompactNumber } from '../utils/format';
import { isFoodCritical, computeDaysOfFood } from '../utils/alerts';
import { COLORS } from '../constants/theme';
import { ResourceBarSheet } from './shell/ResourceBarSheet';
import type { Province } from '../types/province';

const CHROME_GUTTER_CLASSES = 'pl-14 pr-28 lg:pl-6 lg:pr-6';

function horizontalPaddingClasses(reserveChromeGutters: boolean): string {
  return reserveChromeGutters ? CHROME_GUTTER_CLASSES : 'px-6';
}

function Separator({ testId }: { testId: string }) {
  return (
    <div
      data-testid={testId}
      style={{ width: 1, height: 28, backgroundColor: COLORS.separator, flexShrink: 0 }}
    />
  );
}

export interface PillAlert {
  icon: string;
  value: string;
  ariaLabel: string;
}

/**
 * SF-168 Component 2: single-slot priority (only one chip fits) — Food first (existential,
 * unconditional per-tick decay), then Gold (recoverable spending pressure, already validated and
 * reported at the point of spend). Both backed by predicates shared with Throne Room's
 * `deriveAlerts()` (Food) or an existing shared predicate (Gold) — see `alerts.ts`.
 *
 * SF-182: exported so `MobileResourceSummary.tsx` (AppShellLayout's header pill) shares this
 * exact predicate rather than re-deriving it — the alert logic, not the styling, is what must
 * stay a single source of truth.
 */
export function derivePillAlert(province: Province): PillAlert | null {
  if (isFoodCritical(province)) {
    const daysOfFood = computeDaysOfFood(province.economy, province.resources.food);
    const days = daysOfFood !== null ? Math.max(0, Math.floor(daysOfFood)) : null;
    return {
      icon: '🌾',
      value: days !== null ? `${days}d` : formatCompactNumber(province.resources.food),
      ariaLabel:
        days !== null
          ? `Food critical: about ${days} day${days === 1 ? '' : 's'} of food remaining.`
          : `Food critical: ${formatNumber(province.resources.food)} bushels.`,
    };
  }
  if (province.economy.lastTickChanges.goldNet < 0) {
    // The value shown here is the daily net change, not the Gold stock — the stock is now a
    // permanent pill stat (see `ResourceBarPill`), so repeating it here would be redundant.
    return {
      icon: '💰',
      value: `${formatCompactNumber(province.economy.lastTickChanges.goldNet)}/day`,
      ariaLabel: `Gold deficit: ${formatNumber(province.economy.lastTickChanges.goldNet)} gc/day.`,
    };
  }
  return null;
}

/** SF-168 Component 1: mobile pill (below `lg:`) — replaces SF-166's Tier 1/2 mobile content. A
 *  sibling of the desktop row, not a wrapper around it; the two branches share no DOM. Content
 *  copied verbatim from the approved mockup (`docs/prototypes/sf168-resourcebar-mobile-mockup.html:531-539`). */
function ResourceBarPill({
  province,
  pillAlert,
  isSheetOpen,
  onOpen,
}: {
  province: Province;
  pillAlert: PillAlert | null;
  isSheetOpen: boolean;
  onOpen: () => void;
}) {
  const acres = province.territory.totals.acres;
  const networth = province.metadata.networth;
  const gold = province.resources.gold;

  return (
    <button
      type="button"
      data-testid="resource-bar-pill"
      aria-haspopup="dialog"
      aria-expanded={isSheetOpen}
      aria-label={`Province summary. Land ${formatNumber(acres)} acres. Networth ${formatNumber(networth)} gc. Gold ${formatNumber(gold)} gc.${pillAlert ? ` ${pillAlert.ariaLabel}` : ''}`}
      onClick={onOpen}
      data-pill-alert={pillAlert ? 'true' : undefined}
      className="flex items-center justify-center gap-[6px] rounded-full border"
      /* padding/font-size shrink when an alert chip is present — mirrors the mockup's
         `.pill.has-alert { padding: 6px 12px }` rule, expressed via inline style rather than a
         CSS class, since this project has no separate stylesheet for this component */
      style={{ padding: pillAlert ? '6px 12px' : '8px 14px', backgroundColor: COLORS.cardAlt, borderColor: COLORS.border }}
    >
      <span aria-hidden="true">🏰</span>
      <span className="font-mono font-bold" style={{ fontSize: pillAlert ? 12 : 13, color: COLORS.text.primary }}>
        {formatNumber(acres)}<span className="text-[9px] font-semibold" style={{ color: COLORS.text.tertiary }}>ac</span>
      </span>
      <span aria-hidden="true" style={{ color: COLORS.separator }}>·</span>
      <span className="font-mono font-bold" style={{ fontSize: pillAlert ? 12 : 13, color: COLORS.text.primary }}>
        <span className="text-[10px]" style={{ color: COLORS.text.tertiary }}>NW</span> {formatCompactNumber(networth)}
      </span>
      <span aria-hidden="true" style={{ color: COLORS.separator }}>·</span>
      <span className="font-mono font-bold" style={{ fontSize: pillAlert ? 12 : 13, color: COLORS.gold }}>
        {formatCompactNumber(gold)}<span className="text-[9px] font-semibold" style={{ color: COLORS.text.tertiary }}>gc</span>
      </span>
      {pillAlert && (
        <span className="flex items-center gap-1" style={{ color: COLORS.negative }}>
          <span
            aria-hidden="true"
            className="motion-reduce:animate-none animate-pulse"
            style={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0, backgroundColor: COLORS.negative }}
          />
          <span aria-hidden="true">{pillAlert.icon}</span>
          <span className="font-mono font-bold" style={{ fontSize: 12 }}>{pillAlert.value}</span>
        </span>
      )}
      <span aria-hidden="true" className="text-[10px]" style={{ color: COLORS.text.tertiary }}>▾</span>
    </button>
  );
}

/** SF-168 Component 5: desktop row (`lg:` and up) — the pre-existing `TopBarContent` JSX reused
 *  verbatim, wrapped in one outer `hidden lg:flex` container as the desktop-only twin of the
 *  pill's `lg:hidden` branch. SF-166's inner tier-gating classes/`data-tier` attributes are
 *  removed — once the whole row only ever renders at `lg:+` (⊇ `sm:`), those inner gates can
 *  never actually hide anything, so carrying them forward would be dead markup. */
function DesktopRow({ province, reserveChromeGutters }: { province: Province; reserveChromeGutters: boolean }) {
  const acres = province.territory.totals.acres;
  const networth = province.metadata.networth;

  const horizontalPadding = horizontalPaddingClasses(reserveChromeGutters);

  return (
    <div className="hidden lg:flex w-full">
      <div data-testid="resource-bar-row" className={`flex items-center justify-between flex-wrap w-full ${horizontalPadding}`} style={{ gap: 16 }}>
        {/* Left cluster — Land/Networth/NW-Acre, always visible */}
        <div className="flex items-center" style={{ gap: 20 }}>
          <StatItem label="Land" value={formatNumber(acres)} suffix="acres" color={COLORS.text.primary} />
          <StatItem label="Networth" value={formatNumber(networth)} suffix="gc" color={COLORS.gold} />
          {/* rgba(COLORS.gold, 0.85) — translucent to visually distinguish from Networth's opaque gold */}
          <StatItem label="NW/Acre" value={formatNetworthPerAcre(networth, acres)} suffix="gc" color="rgba(212, 167, 44, 0.85)" />
        </div>

        {/* Right cluster — Gold/Food/Runes + Honour. `justify-between` on the row pushes this
            cluster to the far edge, away from the left cluster. */}
        <div className="flex items-center" style={{ gap: 16 }}>
          <div className="flex items-center" style={{ gap: 20 }}>
            <StatItem icon="💰" label="Gold" value={formatNumber(province.resources.gold)} suffix="gc" color={COLORS.gold} />
            <div className="flex items-center" style={{ gap: 20 }}>
              <StatItem icon="🌾" label="Food" value={formatNumber(province.resources.food)} suffix="bushels" color={COLORS.text.primary} />
              <StatItem icon="✨" label="Runes" value={formatNumber(province.resources.runes)} suffix="runes" color={COLORS.text.primary} />
            </div>
          </div>

          <Separator testId="resource-bar-separator-1" />

          <StatItem label="Honour" value={formatNumber(province.resources.honour)} color={COLORS.text.primary} />
        </div>
      </div>
    </div>
  );
}

function StatItem({ icon, label, value, suffix, color }: { icon?: string; label: string; value: string; suffix?: string; color: string }) {
  return (
    <div
      className="flex flex-col items-end whitespace-nowrap"
      data-stat-label={label}
      aria-label={`${label}: ${value}${suffix ? ` ${suffix}` : ''}`}
    >
      <div className="text-[11px] mb-0.5" style={{ color: COLORS.text.tertiary }}>
        {icon && <span className="mr-1" aria-hidden="true">{icon}</span>}
        {label}{suffix && ` (${suffix})`}
      </div>
      <div className="font-bold font-mono text-base" style={{ color }}>
        {value}
      </div>
    </div>
  );
}

export function ResourceBar({ reserveChromeGutters = false, mobileTabBarPresent = false }: { reserveChromeGutters?: boolean; mobileTabBarPresent?: boolean }) {
  const { province, error } = useProvince();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  // SF-168 Decision 7: stabilized via useCallback — ProvinceContext's context-value object
  // literal re-renders every consumer on navigation/refetch, and ResourceBarSheet's copied
  // focus-trap effect's [isOpen, onClose] dependency array would otherwise re-run (and bounce
  // focus) on every one of those re-renders while the sheet is open.
  const closeSheet = useCallback(() => setIsSheetOpen(false), []);
  const horizontalPadding = horizontalPaddingClasses(reserveChromeGutters);

  if (!province) {
    return (
      <div
        className={`flex items-center border-b lg:h-[58px] ${horizontalPadding}`}
        style={{ minHeight: '58px', backgroundColor: COLORS.bgBar, borderColor: COLORS.borderBar }}
        role="group"
        aria-label="Province summary"
      >
        {error ? (
          <div className="text-sm" role="status" style={{ color: COLORS.negative }}>Unable to load province</div>
        ) : (
          <div className="text-sm" role="status" style={{ color: COLORS.text.tertiary }}>Loading...</div>
        )}
      </div>
    );
  }

  const pillAlert = derivePillAlert(province);

  return (
    <div
      className="flex items-center border-b lg:h-[58px]"
      style={{ minHeight: '58px', backgroundColor: COLORS.bgBar, borderColor: COLORS.borderBar }}
      role="group"
      aria-label="Province summary"
    >
      <div className={`lg:hidden flex justify-center w-full ${horizontalPadding}`}>
        <ResourceBarPill
          province={province}
          pillAlert={pillAlert}
          isSheetOpen={isSheetOpen}
          onOpen={() => setIsSheetOpen(true)}
        />
      </div>

      <DesktopRow province={province} reserveChromeGutters={reserveChromeGutters} />

      <ResourceBarSheet
        isOpen={isSheetOpen}
        onClose={closeSheet}
        province={province}
        mobileTabBarPresent={mobileTabBarPresent}
      />
    </div>
  );
}
